import {
  ColumnsButton,
  EmptyRow,
  FilterButton,
  FilterChips,
  PageHead,
  Pager,
  SearchField,
  SkeletonRows,
  SortHead,
  Th,
} from '@/ui';
import PropTypes from 'prop-types';

// Causas list view: a tab bar (Activas / Asignadas / Terminadas), a search +
// single "Filtros" button with applied-filter chips, and a 5-column tbl (Rol ·
// Carátula · Tribunal · Diligencia activa · Últ. actuación), all sortable.
// Presentational: every datum (rows, tabs, filters, sort, page) is handed in by
// the route.
const COLS = [
  { key: 'rol', td: 'tbl__id' },
  { key: 'caratula' },
  { key: 'tribunal' },
  { key: 'diligencia' },
  { key: 'ult', td: 'num note' },
];
// Rol + Carátula are always shown; the rest can be toggled via ColumnsButton.
const ALWAYS_COLS = ['rol', 'caratula'];

export default function CausasLista({
  pageHead,
  actions,
  tabs,
  rows,
  searchValue = '',
  onSearchChange,
  searchPlaceholder = 'Buscar por rol, carátula o tribunal…',
  filterGroups = [],
  columns,
  sortKey,
  sortBy,
  onSortChange,
  pager,
  isLoading = false,
  isError = false,
  onRetry,
  onRowClick,
  activeId,
  selection,
}) {
  const sortProps = { sortKey, sortBy, onSort: onSortChange };
  // A failed fetch is not an empty org. It takes its own row so the reader can
  // tell "nothing to show" from "we could not ask", and so a rejected request
  // stops reading as a legitimate result.
  const empty = !isLoading && !isError && rows.length === 0;
  const searching = (searchValue || '').trim().length > 0;
  // Bulk select (Próx) — a leading checkbox column + a selection bar. Enabled
  // only when the route opts in (experimental features).
  const sel = selection?.enabled ? selection : null;
  // A column is shown when it's always-on, when no columns control is wired, or
  // when it's in the visible set.
  const showCol = (k) =>
    ALWAYS_COLS.includes(k) || !columns || columns.value.includes(k);
  const colCount = COLS.filter((c) => showCol(c.key)).length + (sel ? 1 : 0);

  return (
    <div className="page reveal">
      <PageHead
        kicker={pageHead.kicker}
        title={pageHead.title}
        sub={pageHead.sub}
        action={actions}
      />

      {tabs && (
        <div className="tabs">
          {tabs.items.map((t) => (
            <button
              key={t.key}
              type="button"
              className={`tab${tabs.active === t.key ? ' tab--on' : ''}`}
              onClick={() => tabs.onChange(t.key)}
            >
              {t.label}
              {t.count != null && <span className="tab__n">{t.count}</span>}
            </button>
          ))}
        </div>
      )}

      <div className="toolbar">
        <SearchField
          value={searchValue}
          onChange={onSearchChange}
          placeholder={searchPlaceholder}
        />
        <div className="toolbar__spacer" />
        {columns && (
          <ColumnsButton
            opts={columns.opts}
            value={columns.value}
            onChange={columns.onChange}
            lockedNote={columns.lockedNote}
          />
        )}
        {filterGroups.length > 0 && <FilterButton groups={filterGroups} />}
      </div>

      <FilterChips groups={filterGroups} />

      {sel?.bar}

      <div className="tbl-wrap">
        <table className="tbl">
          <thead>
            <tr>
              {sel && (
                <th style={{ width: 32 }}>
                  <label
                    className="t-check"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <input
                      type="checkbox"
                      checked={sel.allChecked}
                      onChange={sel.onToggleAll}
                    />
                  </label>
                </th>
              )}
              <SortHead label="Rol" k="rol" w="14%" {...sortProps} />
              {/* The header's `k` travels verbatim to the server as `sortKey`, so it
                  must be a name the endpoint accepts. The caller's column key stays
                  `caratula` for visibility; only the sort name is `title`. */}
              <SortHead label="Carátula" k="title" {...sortProps} />
              {showCol('tribunal') && (
                <SortHead
                  label="Tribunal"
                  k="tribunalName"
                  w="22%"
                  {...sortProps}
                />
              )}
              {showCol('diligencia') && (
                <SortHead
                  label="Diligencia activa"
                  k="diligencia"
                  w="22%"
                  {...sortProps}
                />
              )}
              {showCol('ult') && (
                <Th
                  w="13%"
                  num
                  tip="Última actuación recibida del Poder Judicial"
                >
                  Últ. actuación
                </Th>
              )}
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <SkeletonRows rows={8} cols={colCount} />
            ) : isError ? (
              <EmptyRow
                span={colCount}
                title="No se pudo cargar la lista de causas"
                hint={
                  onRetry ? (
                    <button
                      type="button"
                      className="tbl-empty__retry"
                      onClick={onRetry}
                    >
                      Reintentar
                    </button>
                  ) : (
                    'Reintenta en unos segundos.'
                  )
                }
              />
            ) : empty ? (
              <EmptyRow
                span={colCount}
                title={
                  searching
                    ? `Sin resultados para “${searchValue}”`
                    : 'Ninguna causa en este filtro'
                }
                hint={
                  searching
                    ? 'Revisa el rol o la carátula.'
                    : 'Ajusta los filtros o suscribe una causa.'
                }
              />
            ) : (
              rows.map((r) => (
                <tr
                  key={r.id}
                  data-urgent={r.urgent ? 'true' : undefined}
                  className={r.id === activeId ? 'row--on' : undefined}
                  onClick={() => onRowClick?.(r)}
                >
                  {sel && (
                    <td onClick={(e) => e.stopPropagation()}>
                      <label className="t-check">
                        <input
                          type="checkbox"
                          checked={sel.isSelected(r.id)}
                          onChange={() => sel.onToggle(r.id)}
                        />
                      </label>
                    </td>
                  )}
                  {COLS.filter((c) => showCol(c.key)).map((c) => (
                    <td key={c.key} className={c.td}>
                      {r.cells[c.key]}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
        {!isLoading && pager && (
          <Pager
            page={pager.page}
            pageCount={pager.pageCount}
            total={pager.total}
            pageSize={pager.pageSize}
            onPage={pager.onPage}
            onPageSize={pager.onPageSize}
            sizes={[8, 16, 24, 48]}
            unit="causas"
          />
        )}
      </div>
    </div>
  );
}

CausasLista.propTypes = {
  pageHead: PropTypes.shape({
    kicker: PropTypes.node,
    title: PropTypes.node,
    sub: PropTypes.node,
  }).isRequired,
  actions: PropTypes.node,
  tabs: PropTypes.shape({
    active: PropTypes.string,
    onChange: PropTypes.func,
    items: PropTypes.arrayOf(
      PropTypes.shape({
        key: PropTypes.string,
        label: PropTypes.node,
        count: PropTypes.number,
      }),
    ),
  }),
  rows: PropTypes.array.isRequired,
  searchValue: PropTypes.string,
  onSearchChange: PropTypes.func,
  searchPlaceholder: PropTypes.string,
  filterGroups: PropTypes.array,
  columns: PropTypes.shape({
    value: PropTypes.arrayOf(PropTypes.string),
    onChange: PropTypes.func,
    opts: PropTypes.arrayOf(PropTypes.array),
    lockedNote: PropTypes.string,
  }),
  sortKey: PropTypes.string,
  sortBy: PropTypes.oneOf(['asc', 'desc']),
  onSortChange: PropTypes.func,
  pager: PropTypes.object,
  isLoading: PropTypes.bool,
  isError: PropTypes.bool,
  onRetry: PropTypes.func,
  onRowClick: PropTypes.func,
  activeId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  // Bulk select (Próx): enabled ⇒ leading checkbox column + a selection bar.
  selection: PropTypes.shape({
    enabled: PropTypes.bool,
    isSelected: PropTypes.func,
    onToggle: PropTypes.func,
    onToggleAll: PropTypes.func,
    allChecked: PropTypes.bool,
    bar: PropTypes.node,
  }),
};
